import { z } from 'zod';

import { LocaleSchema } from '../entities/traveler/travel_preferences.js';

/**
 * A cotação de uma busca de disponibilidade em PDF (#5156). Viaja web-chat → BFF: a
 * tela achata a lista que a pessoa está vendo (na ordem e com os filtros dela) em
 * linhas JÁ escritas no idioma dela, e o servidor só desenha — ele não tem a busca
 * (oferta é perecível e não fica guardada) nem conhece voo, hotel, carro ou ônibus.
 * Nunca carrega quem viaja: a cotação vai para fora da VOLL.
 */

/** Teto de linhas por PDF: a cotação viaja dentro do link assinado, e a lista é o
 *  que a pessoa tem na tela — as primeiras, não a busca inteira. */
export const SEARCH_QUOTE_MAX_ROWS = 30;

export const SearchQuoteModalSchema = z.enum(['flight', 'hotel', 'rental', 'bus']);
export type SearchQuoteModal = z.infer<typeof SearchQuoteModalSchema>;

/** O tom do selo, nos três tons que a tela usa para destaque e política. */
export const SearchQuoteToneSchema = z.enum(['success', 'neutral', 'danger']);
export type SearchQuoteTone = z.infer<typeof SearchQuoteToneSchema>;

const text = (max: number) => z.string().trim().min(1).max(max);

export const SearchQuoteBadgeSchema = z
  .object({ label: text(40), tone: SearchQuoteToneSchema })
  .strict();
export type SearchQuoteBadge = z.infer<typeof SearchQuoteBadgeSchema>;

/**
 * A imagem da linha. A companhia aérea vai pelo código IATA, e o servidor monta o
 * endereço do logo; a foto (hotel, carro, viação) vai pelo endereço que a tela já
 * mostra, e o servidor só a baixa sob a própria guarda. Sem imagem, o PDF desenha o
 * ícone do modal, como a tela.
 */
export const SearchQuoteMediaSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('airline'), iata: z.string().regex(/^[A-Z0-9]{2}$/) }).strict(),
  z.object({ kind: z.literal('photo'), url: z.url({ protocol: /^https$/ }).max(2048) }).strict(),
]);
export type SearchQuoteMedia = z.infer<typeof SearchQuoteMediaSchema>;

export const SearchQuoteRowSchema = z
  .object({
    media: SearchQuoteMediaSchema.optional(),
    /** A linha de cima: companhia e voo, nota do hotel, locadora, viação. */
    eyebrow: text(80),
    title: text(140),
    details: z.array(text(240)).max(3),
    badges: z.array(SearchQuoteBadgeSchema).max(4),
    /** O porquê do destaque, quando a tela o mostra ("Voo direto, tarifa flex…"). */
    note: text(240).optional(),
    price: text(40),
    /** O que o preço cobre ("só ida, por pessoa", "4 noites · R$ 489 por noite"). */
    priceNote: text(80).optional(),
  })
  .strict();
export type SearchQuoteRow = z.infer<typeof SearchQuoteRowSchema>;

export const SearchQuoteSchema = z
  .object({
    modal: SearchQuoteModalSchema,
    /** O sobretítulo da busca ("Voo · Só ida · Econômica"). */
    eyebrow: text(80),
    /** O título grande — origem → destino, ou o destino. */
    title: text(140),
    /** Datas, ocupação e o resto da pergunta, sem nomes. */
    meta: text(240),
    countLabel: text(80),
    /** De onde vem a lista ("Na ordem da tela · sem filtros"). */
    scopeNote: text(140).optional(),
    rows: z.array(SearchQuoteRowSchema).min(1).max(SEARCH_QUOTE_MAX_ROWS),
    /** O link que refaz a busca. O servidor só o imprime se ele apontar para o app
     *  da VOLL (`safeVollSearchUrl`). */
    link: z.string().max(2048).optional(),
  })
  .strict();
export type SearchQuote = z.infer<typeof SearchQuoteSchema>;

/** Corpo do `POST /search-quote/pdf/link`: a cotação e o idioma das legendas fixas
 *  do PDF (carimbo, rodapé), que o servidor escreve. */
export const SearchQuotePdfLinkRequestSchema = z
  .object({ quote: SearchQuoteSchema, lang: LocaleSchema })
  .strict();
export type SearchQuotePdfLinkRequest = z.infer<typeof SearchQuotePdfLinkRequestSchema>;
